import { zodResolver } from "@hookform/resolvers/zod";
import type { ComponentProps } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { enqueueSnackbar } from "../../../../../components/GlobalSnackbar/snackbarStore";
import { isSupabaseError } from "../../../../../constants/supabaseErrors";
import type AddCallActionModal from "./AddCallActionModal";
import useSubjectActionModal from "../useSubjectActionModal";
import { formSchema as formSchemaBase } from "../types";
import { CommunicationType } from "../../../../../types";
import emptyHtmlToNull from "../../../../../utils/emptyHtmlToNull";

const formSchema = formSchemaBase.extend({
  contact_name: z.string().trim().min(1, "Ce champ est obligatoire"),
  comment: z.string().trim().optional().transform(emptyHtmlToNull),
  communication_type: z.custom<CommunicationType>().refine(val => !!val, "Ce champ est obligatoire"),
})

type FormInputs = z.input<typeof formSchema>;
type FormValues = z.infer<typeof formSchema>;

type Props = ComponentProps<typeof AddCallActionModal>;

export default function useAddCallActionModal(props: Props) {
  const [serverError, setServerError] = useState<string | null>(null);

  const [showDocuments, setShowDocuments] = useState(false);

  const form = useForm<FormInputs, unknown, FormValues>({
    mode: "onChange",
    defaultValues: {
      date: props.item?.date ? new Date(props.item.date) : new Date(),
      contact_name: props.item?.contact_name || "",
      provider_name: props.item?.provider_name || "",
      comment: props.item?.comment || "",
      documents: [],
      communication_type: props.item?.communication_type || "email",
    },
    resolver: zodResolver(formSchema),
  });

  const {
    isDocumentLoading,
    isLoading,
    handleCallApi,
  } = useSubjectActionModal(
    "call",
    props.item?.id ?? undefined,
    props.subjectId,
    form,
  );

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      await handleCallApi(data);

      enqueueSnackbar(props.item ? "Communication mise à jour." : "Communication enregistrée.", "success");
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }

      setServerError(
        "Une erreur est survenue",
      );
    }
  }

  const documents = form.watch("documents");

  return {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
    showDocuments: showDocuments || documents.length > 0,
    revealDocuments: () => setShowDocuments(true),
  };
}
